import {
    DATALAKE_BACEN_CADASTRO_CONGLOMERADO_PATH,
    DATALAKE_CONTRAPARTE_PATH,
    DATALAKE_CVM_CADASTRO_EMPRESAS_PATH,
} from "src/utils/api-routes"

const FIELD_TYPE = {
    BOOLEAN: "boolean",
    DATE: "date",
    NUMBER: "number",
    TEXT: "text",
}

const FIELD_CONTROL = {
    AUTOCOMPLETE: "autocomplete",
    SELECT: "select",
}

const formatPhone = (countryCode, areaCode, number) => {
    if (!number) return ""

    const countryPrefix = countryCode ? `+${countryCode} ` : ""
    const areaPrefix = areaCode ? `(${areaCode}) ` : ""

    return `${countryPrefix}${areaPrefix}${number}`
}

const formatClosingMonth = (value) => {
    const monthNames = [
        "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
        "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
    ]

    return monthNames[Number(value) - 1] ?? value
}

const makeField = (name, label, type = FIELD_TYPE.TEXT, section = "Dados cadastrais", options = {}) => ({
    name,
    label,
    type,
    section,
    ...options,
})

const withMetadataColumns = (columns, { includeCreated = true, includeUpdated = true } = {}) => [
    ...columns,
    ...(includeCreated ? [{ field: "CriadoEm", headerName: "Data de criação", type: FIELD_TYPE.DATE }] : []),
    ...(includeUpdated ? [{ field: "AtualizadoEm", headerName: "Data de atualização", type: FIELD_TYPE.DATE }] : []),
]

const counterpartFields = [
    makeField("CPFCNPJ", "CPF/CNPJ", FIELD_TYPE.TEXT, "Dados cadastrais", { required: true, readOnlyOnEdit: true }),
    makeField("RaizCNPJ", "Raiz CNPJ", FIELD_TYPE.TEXT, "Dados cadastrais", { required: true }),
    makeField("RazaoSocial", "Razão social", FIELD_TYPE.TEXT, "Dados cadastrais", { required: true }),
    makeField("NomeFantasia", "Nome fantasia", FIELD_TYPE.TEXT, "Dados cadastrais", { required: true }),
    makeField("GrupoEconomico_Nome", "Grupo econômico", FIELD_TYPE.TEXT, "Informações complementares", { control: FIELD_CONTROL.AUTOCOMPLETE }),
    makeField("GrupoEconomico_RaizCNPJ", "Raiz CNPJ do grupo", FIELD_TYPE.TEXT, "Informações complementares"),
    makeField("Site_RI", "Site RI", FIELD_TYPE.TEXT, "Descrição da empresa", { detailLink: true, fullWidth: true, fullWidthInDetails: true }),
    makeField("Observacao", "Observação", FIELD_TYPE.TEXT, "Descrição da empresa", { maxLength: 300, multiline: true, fullWidth: true, fullWidthInDetails: true }),
    makeField("Atividade", "Atividade", FIELD_TYPE.TEXT, "Descrição da empresa", { maxLength: 600, multiline: true, fullWidth: true, fullWidthInDetails: true }),
    makeField("Setor", "Setor", FIELD_TYPE.TEXT, "Classificação", { required: true, control: FIELD_CONTROL.SELECT, optionsSource: "sectors" }),
    makeField("SubSetor", "Subsetor", FIELD_TYPE.TEXT, "Classificação", { required: true, control: FIELD_CONTROL.SELECT, optionsSource: "subsectors" }),
    makeField("MesFechamento", "Mês de fechamento", FIELD_TYPE.NUMBER, "Classificação", { detailValue: formatClosingMonth }),
    makeField("TipoCadastro", "Tipo de cadastro", FIELD_TYPE.TEXT, "Classificação", { control: FIELD_CONTROL.SELECT, options: [{ value: "MATRIZ", label: "MATRIZ" }, { value: "FILIAL", label: "FILIAL" }] }),
    makeField("PeriodicidadeBalanco", "Periodicidade do balanço", FIELD_TYPE.NUMBER, "Classificação", { control: FIELD_CONTROL.SELECT, optionsSource: "periodicities" }),
    makeField("EGEM", "EGEM", FIELD_TYPE.BOOLEAN, "Classificação"),
    makeField("EFRF", "EFRF", FIELD_TYPE.BOOLEAN, "Classificação"),
    makeField("GrupoRating", "Grupo rating", FIELD_TYPE.NUMBER, "Grupos relacionados", { visibleInDetails: false }),
    makeField("GrupoAuditoria", "Grupo auditoria", FIELD_TYPE.NUMBER, "Grupos relacionados", { visibleInDetails: false }),
    makeField("GrupoCustodiante", "Grupo custodiante", FIELD_TYPE.NUMBER, "Grupos relacionados", { visibleInDetails: false }),
    makeField("GrupoCoordenadorLider", "Grupo coordenador líder", FIELD_TYPE.NUMBER, "Grupos relacionados", { visibleInDetails: false }),
    makeField("GrupoFiduciario", "Grupo fiduciário", FIELD_TYPE.NUMBER, "Grupos relacionados", { visibleInDetails: false }),
    makeField("GrupoIFRecebedora", "Grupo IF recebedora", FIELD_TYPE.NUMBER, "Grupos relacionados", { visibleInDetails: false }),
    makeField("GrupoSecuritizador", "Grupo securitizador", FIELD_TYPE.NUMBER, "Grupos relacionados", { visibleInDetails: false }),
]

const cvmFields = [
    makeField("CPFCNPJ", "CPF/CNPJ", FIELD_TYPE.TEXT, "Dados cadastrais", { required: true, readOnlyOnEdit: true }),
    makeField("TipoFiscal", "Tipo fiscal", FIELD_TYPE.TEXT, "Dados cadastrais", { required: true }),
    makeField("RazaoSocial", "Razão social"),
    makeField("NomeFantasia", "Nome fantasia", FIELD_TYPE.TEXT, "Dados cadastrais", {
        visibleInDetailsWhen: (value, row) => value !== row.RazaoSocial,
    }),
    makeField("PaisOrigem", "País de origem"),
    makeField("DataRegistroCVM", "Data de registro CVM", FIELD_TYPE.DATE),
    makeField("DataConstituicao", "Data de constituição", FIELD_TYPE.DATE),
    makeField("CodigoCVM", "Código CVM"),
    makeField("SetorCVM", "Setor CVM"),
    makeField("TipoMercado", "Tipo de mercado", FIELD_TYPE.TEXT, "Mercado"),
    makeField("ControleAcionario", "Controle acionário", FIELD_TYPE.TEXT, "Mercado"),
    makeField("TipoEndereco", "Tipo de endereço", FIELD_TYPE.TEXT, "Endereço"),
    makeField("Logradouro", "Logradouro", FIELD_TYPE.TEXT, "Endereço"),
    makeField("Complemento", "Complemento", FIELD_TYPE.TEXT, "Endereço"),
    makeField("Bairro", "Bairro", FIELD_TYPE.TEXT, "Endereço"),
    makeField("Municipio", "Município", FIELD_TYPE.TEXT, "Endereço"),
    makeField("UF", "UF", FIELD_TYPE.TEXT, "Endereço"),
    makeField("Pais", "País", FIELD_TYPE.TEXT, "Endereço"),
    makeField("CEP", "CEP", FIELD_TYPE.TEXT, "Endereço"),
    makeField("DDITelefone", "DDI telefone", FIELD_TYPE.TEXT, "Contato", { visibleInDetails: false }),
    makeField("DDDTelefone", "DDD telefone", FIELD_TYPE.TEXT, "Contato", { visibleInDetails: false }),
    makeField("Telefone", "Telefone", FIELD_TYPE.TEXT, "Contato", {
        detailValue: (value, row) => formatPhone(row.DDITelefone, row.DDDTelefone, value),
    }),
    makeField("DDIFax", "DDI fax", FIELD_TYPE.TEXT, "Contato", { visibleInDetails: false }),
    makeField("DDDFax", "DDD fax", FIELD_TYPE.TEXT, "Contato", { visibleInDetails: false }),
    makeField("Fax", "Fax", FIELD_TYPE.TEXT, "Contato", { visibleInDetails: false }),
    makeField("Email", "E-mail", FIELD_TYPE.TEXT, "Contato"),
    makeField("CargoResponsavel", "Cargo do responsável", FIELD_TYPE.TEXT, "Responsável"),
    makeField("Responsavel", "Responsável", FIELD_TYPE.TEXT, "Responsável"),
    makeField("DataResponsavel", "Data do responsável", FIELD_TYPE.DATE, "Responsável"),
    makeField("LogradouroResponsavel", "Logradouro", FIELD_TYPE.TEXT, "Endereço do responsável", { visibleInDetails: false }),
    makeField("ComplementoResponsavel", "Complemento", FIELD_TYPE.TEXT, "Endereço do responsável", { visibleInDetails: false }),
    makeField("BairroResponsavel", "Bairro", FIELD_TYPE.TEXT, "Endereço do responsável", { visibleInDetails: false }),
    makeField("MunicipioResponsavel", "Município", FIELD_TYPE.TEXT, "Endereço do responsável", { visibleInDetails: false }),
    makeField("UFResponsavel", "UF", FIELD_TYPE.TEXT, "Endereço do responsável", { visibleInDetails: false }),
    makeField("PaisResponsavel", "País", FIELD_TYPE.TEXT, "Endereço do responsável", { visibleInDetails: false }),
    makeField("CEPResponsavel", "CEP", FIELD_TYPE.TEXT, "Endereço do responsável", { visibleInDetails: false }),
    makeField("DDDTelefoneResponsavel", "DDD telefone", FIELD_TYPE.TEXT, "Contato do responsável", { visibleInDetails: false }),
    makeField("TelefoneResponsavel", "Telefone", FIELD_TYPE.TEXT, "Contato do responsável", {
        detailValue: (value, row) => formatPhone(null, row.DDDTelefoneResponsavel, value),
    }),
    makeField("DDDFaxResponsavel", "DDD fax", FIELD_TYPE.TEXT, "Contato do responsável", { visibleInDetails: false }),
    makeField("FaxResponsavel", "Fax", FIELD_TYPE.TEXT, "Contato do responsável", { visibleInDetails: false }),
    makeField("EmailResponsavel", "E-mail", FIELD_TYPE.TEXT, "Contato do responsável"),
    makeField("CNPJAuditor", "CNPJ do auditor", FIELD_TYPE.TEXT, "Auditoria"),
    makeField("Auditor", "Auditor", FIELD_TYPE.TEXT, "Auditoria"),
]

const bacenFields = [
    makeField("CPFCNPJ", "CPF/CNPJ", FIELD_TYPE.TEXT, "Dados cadastrais", { readOnlyOnEdit: true }),
    makeField("MesBase", "Mês-base"),
    makeField("NomeIF", "Nome IF"),
    makeField("CodigoIF", "Código IF"),
    makeField("InicioAtividade", "Início da atividade", FIELD_TYPE.TEXT, "Dados cadastrais", { visibleInDetails: false }),
    makeField("TCB", "TCB", FIELD_TYPE.TEXT, "Códigos regulatórios"),
    makeField("TD", "TD", FIELD_TYPE.TEXT, "Códigos regulatórios"),
    makeField("TC", "TC", FIELD_TYPE.NUMBER, "Códigos regulatórios"),
    makeField("SegmentoTB", "Segmento TB", FIELD_TYPE.TEXT, "Códigos regulatórios", {
        visibleInDetailsWhen: (value) => `${value}`.trim().toLowerCase() !== "nan",
    }),
    makeField("Atividade", "Atividade", FIELD_TYPE.TEXT, "Informações operacionais"),
    makeField("SR", "SR", FIELD_TYPE.TEXT, "Informações operacionais"),
    makeField("Situacao", "Situação", FIELD_TYPE.TEXT, "Informações operacionais"),
    makeField("Municipio", "Município", FIELD_TYPE.TEXT, "Localização"),
    makeField("UF", "UF", FIELD_TYPE.TEXT, "Localização"),
    makeField("CodigoConglomeradoFinanceiro", "Código do conglomerado financeiro", FIELD_TYPE.TEXT, "Conglomerado"),
    makeField("CodigoConglomeradoPrudencial", "Código do conglomerado prudencial", FIELD_TYPE.TEXT, "Conglomerado"),
    makeField("CNPJInstituicaoLider", "Raiz CNPJ da instituição líder", FIELD_TYPE.TEXT, "Conglomerado"),
]

export const COMPANY_REGISTRATION_CONFIG = {
    CONTRAPARTE: {
        title: "Contraparte",
        endpoint: DATALAKE_CONTRAPARTE_PATH,
        allowCreate: false,
        allowView: true,
        allowDelete: false,
        showPaginationWhenMultiple: false,
        fields: counterpartFields,
        listColumns: withMetadataColumns([
            { field: "CPFCNPJ", headerName: "CPF/CNPJ" },
            { field: "RazaoSocial", headerName: "Razão social" },
            { field: "NomeFantasia", headerName: "Nome fantasia" },
            { field: "Setor", headerName: "Setor" },
            { field: "SubSetor", headerName: "Subsetor" },
            { field: "GrupoEconomico_Nome", headerName: "Grupo econômico" },
            { field: "TipoCadastro", headerName: "Tipo de cadastro" },
        ], { includeCreated: false }),
    },
    "CADASTRO CVM": {
        title: "Cadastro CVM",
        endpoint: DATALAKE_CVM_CADASTRO_EMPRESAS_PATH,
        allowCreate: false,
        allowView: true,
        allowEdit: false,
        allowDelete: false,
        showPaginationWhenMultiple: true,
        fields: cvmFields,
        listColumns: withMetadataColumns([
            { field: "CPFCNPJ", headerName: "CPF/CNPJ" },
            { field: "RazaoSocial", headerName: "Razão social" },
            { field: "CodigoCVM", headerName: "Código CVM" },
            { field: "TipoFiscal", headerName: "Tipo fiscal" },
            { field: "PaisOrigem", headerName: "País origem" },
            { field: "SetorCVM", headerName: "Setor CVM" },
            { field: "TipoMercado", headerName: "Tipo de mercado" },
            { field: "ControleAcionario", headerName: "Controle acionário", minWidth: 190 },
            { field: "DataRegistroCVM", headerName: "Data registro CVM", type: FIELD_TYPE.DATE, minWidth: 190 },
            { field: "DataConstituicao", headerName: "Data de constituição", type: FIELD_TYPE.DATE, minWidth: 210 },
        ], { includeCreated: false, includeUpdated: false }),
    },
    "CADASTRO BACEN": {
        title: "Cadastro Bacen",
        endpoint: DATALAKE_BACEN_CADASTRO_CONGLOMERADO_PATH,
        allowCreate: false,
        allowView: true,
        allowEdit: false,
        allowDelete: false,
        showPaginationWhenMultiple: true,
        fields: bacenFields,
        listColumns: withMetadataColumns([
            { field: "CPFCNPJ", headerName: "CPF/CNPJ" },
            { field: "MesBase", headerName: "Mês-base" },
            { field: "NomeIF", headerName: "Nome IF" },
            { field: "CodigoIF", headerName: "Código IF" },
            { field: "Atividade", headerName: "Atividade" },
            { field: "SR", headerName: "SR" },
            { field: "Situacao", headerName: "Situação" },
            { field: "Municipio", headerName: "Município" },
            { field: "UF", headerName: "UF" },
        ], { includeCreated: false, includeUpdated: false }),
    },
}

export { FIELD_CONTROL, FIELD_TYPE }
